import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Grid, Spinner, Text, useDisclosure, VStack } from "@chakra-ui/react";
import showToast from "hooks/useCustomToast";
import DocModal from "components/eva/DocModal";
import EvaEntityLeftPanel from "components/eva/EvaEntityLeftPanel";
import EvaPromptRightPanelWrapper from "components/eva/EvaPromptRightPanelWrapper";
import { DarwinAgent, DarwinTool, PromptProto, SkillProto } from "build-ts/darwin_agent";
import { JinjaEnv } from "build-ts/jinja";
import {
  DarwinAgentType,
  DarwinHeader,
  darwinAgentTypeFromJSON,
  ReasoningEffortLevel,
  reasoningEffortLevelFromJSON,
} from "build-ts/common";
import {
  getAgentById,
  getSkillById,
  getSkillsList,
  getSystemPromptDocumentation,
  getToolsList,
  updateAgent,
  updateSkill,
} from "services/EvaServices";

interface EvaEntityDetailContainerProps {
  isSkillMode?: boolean;
}

const EvaEntityDetailContainer: React.FC<EvaEntityDetailContainerProps> = ({ isSkillMode = false }) => {
  const { agent_id, skill_id } = useParams<{ agent_id?: string; skill_id?: string }>();
  const entityId = isSkillMode ? skill_id : agent_id;

  const [promptEditor, setPromptEditor] = useState("");
  const [agentConfig, setAgentConfig] = useState<DarwinAgent>({
    header: { id: "", name: "", description: "", type: "", is_active: false },
    prompts: [],
    tools: [],
    skills: [],
    context_template: "",
  });
  const [skillConfig, setSkillConfig] = useState<SkillProto>({
    header: { id: "", name: "", description: "" },
    agentic_name: "",
    prompts: [],
    tools: [],
  });
  const [promptConfig, setPromptConfig] = useState<PromptProto>({
    header: { id: "", name: "", description: "", is_active: false },
    model_config: {
      model_name: "gpt-4o",
      model_provider: "openai",
      temperature: 0,
      reasoning_effort_level: ReasoningEffortLevel.REASONING_NOT_VALID,
    },
    template: "",
  });

  const [isLoading, setIsLoading] = useState(true);
  const [availableTools, setAvailableTools] = useState<DarwinTool[]>([]);
  const [availableSkills, setAvailableSkills] = useState<SkillProto[]>([]);
  const [selectedTools, setSelectedTools] = useState<DarwinTool[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<SkillProto[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [originalAgentConfig, setOriginalAgentConfig] = useState<DarwinAgent | null>(null);
  const [originalSkillConfig, setOriginalSkillConfig] = useState<SkillProto | null>(null);
  const [originalPromptConfig, setOriginalPromptConfig] = useState<PromptProto | null>(null);
  const [originalPromptEditor, setOriginalPromptEditor] = useState("");
  const [originalSelectedTools, setOriginalSelectedTools] = useState<DarwinTool[]>([]);
  const [originalSelectedSkills, setOriginalSelectedSkills] = useState<SkillProto[]>([]);

  const [availableVariables, setAvailableVariables] = useState<JinjaEnv | null>(null);
  const [isLoadingDoc, setIsLoadingDoc] = useState(false);
  const { isOpen: isDocsOpen, onOpen: onDocsOpen, onClose: onDocsClose } = useDisclosure();

  const fetchToolsList = async () => {
    const params = new URLSearchParams({
      limit: "100",
      offset: "0",
      sort_by: "name",
      sort_order: "asc",
    });
    const data = await getToolsList(params);
    return data.tools || [];
  };

  const fetchSkillsList = async (): Promise<SkillProto[]> => {
    const params = new URLSearchParams({
      limit: "100",
      offset: "0",
      sort_by: "CREATED_DATE",
      sort_order: "ASC",
    });
    try {
      const data = await getSkillsList(params);
      const headers = data.headers || [];
      return headers.map((header: DarwinHeader) => ({
        header: header,
        prompts: [],
        tools: [],
      }));
    } catch {
      return [];
    }
  };

  const fetchSystemPromptDocumentation = async () => {
    try {
      setIsLoadingDoc(true);
      const data = await getSystemPromptDocumentation();
      setAvailableVariables(data || null);
    } catch (error) {
      console.error("Error fetching system prompt documentation:", error);
      showToast("Failed to fetch documentation", "", "error");
      setAvailableVariables(null);
    } finally {
      setIsLoadingDoc(false);
    }
  };

  const handleViewDocs = async () => {
    onDocsOpen();
    await fetchSystemPromptDocumentation();
  };

  useEffect(() => {
    const loadData = async () => {
      if (!entityId) {
        showToast(`No ${isSkillMode ? "skill" : "agent"} ID provided`, "", "error");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const [entityData, toolsData, skillsData] = await Promise.all([
          isSkillMode ? getSkillById(entityId) : getAgentById(entityId),
          fetchToolsList(),
          isSkillMode ? Promise.resolve([] as SkillProto[]) : fetchSkillsList(),
        ]);

        if (!entityData) {
          showToast("Error", `Failed to load ${isSkillMode ? "skill" : "agent"} data`, "error");
          return;
        }

        const firstPrompt = entityData.prompts?.[0];
        const promptInitialState: PromptProto = {
          header: {
            id: firstPrompt?.header?.id || "",
            name: firstPrompt?.header?.name || "",
            description: firstPrompt?.header?.description || "",
            is_active: firstPrompt?.header?.is_active ?? false,
            created_date: firstPrompt?.header?.created_date || 0,
            last_modified_date: firstPrompt?.header?.last_modified_date || 0,
          },
          model_config: {
            model_name: firstPrompt?.model_config?.model_name || "gpt-4o",
            model_provider: firstPrompt?.model_config?.model_provider || "openai",
            temperature: firstPrompt?.model_config?.temperature ?? 0,
            reasoning_effort_level:
              reasoningEffortLevelFromJSON(firstPrompt?.model_config?.reasoning_effort_level) ??
              ReasoningEffortLevel.REASONING_NOT_VALID,
          },
          template: firstPrompt?.template || "",
        };
        setPromptConfig(promptInitialState);
        setPromptEditor(firstPrompt?.template || "");

        if (isSkillMode) {
          const skillInitialState: SkillProto = {
            header: {
              id: entityData.header?.id || "",
              name: entityData.header?.name?.trim() || "",
              description: entityData.header?.description?.trim() || "",
              created_date: entityData.header?.created_date || 0,
              last_modified_date: entityData.header?.last_modified_date || 0,
            },
            agentic_name: entityData.agentic_name || "",
            prompts: entityData.prompts || [],
            tools: entityData.tools || [],
          };
          setSkillConfig(skillInitialState);
          setOriginalSkillConfig(skillInitialState);
        } else {
          const agentInitialState: DarwinAgent = {
            header: {
              id: entityData.header?.id || "",
              name: entityData.header?.name?.trim() || "",
              description: entityData.header?.description?.trim() || "",
              type: entityData.header?.type || "",
              is_active: entityData.header?.is_active ?? false,
              created_date: entityData.header?.created_date || 0,
              last_modified_date: entityData.header?.last_modified_date || 0,
            },
            prompts: entityData.prompts || [],
            tools: entityData.tools || [],
            skills: entityData.skills || [],
            context_template: entityData.context_template || "",
          };
          setAgentConfig(agentInitialState);
          setOriginalAgentConfig(agentInitialState);
        }

        setSelectedTools(entityData.tools?.map((tool: DarwinTool) => tool) || []);
        setAvailableTools(toolsData);
        setAvailableSkills(skillsData);
        setSelectedSkills((entityData.skills as SkillProto[] | undefined) || []);
        setOriginalPromptConfig(promptInitialState);
        setOriginalPromptEditor(firstPrompt?.template || "");
        setOriginalSelectedTools(entityData.tools?.map((tool: DarwinTool) => tool) || []);
        setOriginalSelectedSkills((entityData.skills as SkillProto[] | undefined) || []);
      } catch (error) {
        console.error("Error loading entity data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, isSkillMode]);

  const handleConfigChange = (field: string, value: any) => {
    const updater = <T extends { header?: any }>(prev: T): T => {
      if (field.includes(".")) {
        const [parentField, childField] = field.split(".");
        if (parentField === "header") {
          return { ...prev, header: { ...prev.header, [childField]: value } };
        }
      }
      return { ...prev, [field]: value };
    };

    if (isSkillMode) {
      setSkillConfig((prev) => updater(prev));
    } else {
      setAgentConfig((prev) => updater(prev));
    }
  };

  const handleToolsChange = (selectedToolsArray: DarwinTool[]) => {
    setSelectedTools(selectedToolsArray);
    if (isSkillMode) {
      setSkillConfig((prev) => ({ ...prev, tools: selectedToolsArray }));
    } else {
      setAgentConfig((prev) => ({ ...prev, tools: selectedToolsArray }));
    }
  };

  const handleSkillsChange = (skills: SkillProto[]) => {
    setSelectedSkills(skills);
    if (!isSkillMode) {
      setAgentConfig((prev) => ({ ...prev, skills }));
    }
  };

  const handlePromptEditorChange = (value: string) => {
    setPromptEditor(value);
    setPromptConfig((prev) => ({ ...prev, template: value }));
  };

  const handlePromptConfigChange = (field: string, value: any) => {
    setPromptConfig((prev) => {
      if (field.includes(".")) {
        const [parentField, childField] = field.split(".");
        if (parentField === "header") {
          return { ...prev, header: { ...prev.header, [childField]: value } };
        }
        if (parentField === "model_config") {
          return { ...prev, model_config: { ...prev.model_config, [childField]: value } };
        }
      }
      return { ...prev, [field]: value };
    });
  };

  const handleEdit = () => {
    if (isSkillMode) {
      setOriginalSkillConfig({ ...skillConfig });
    } else {
      setOriginalAgentConfig({ ...agentConfig });
    }
    setOriginalPromptConfig({ ...promptConfig });
    setOriginalPromptEditor(promptEditor);
    setOriginalSelectedTools([...selectedTools]);
    setOriginalSelectedSkills([...selectedSkills]);
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (isSkillMode && originalSkillConfig) setSkillConfig(originalSkillConfig);
    if (!isSkillMode && originalAgentConfig) setAgentConfig(originalAgentConfig);
    if (originalPromptConfig) setPromptConfig(originalPromptConfig);
    setPromptEditor(originalPromptEditor);
    setSelectedTools(originalSelectedTools);
    setSelectedSkills(originalSelectedSkills);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const result = isSkillMode
      ? await updateSkill(entityId, skillConfig, selectedTools)
      : await updateAgent(entityId, { ...agentConfig, skills: selectedSkills }, selectedTools);
    if (result) setIsEditing(false);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="50vh">
        <VStack spacing={4}>
          <Spinner size="xl" color={isSkillMode ? "purple.500" : "blue.500"} />
          <Text>Loading {isSkillMode ? "skill" : "agent"} data...</Text>
        </VStack>
      </Box>
    );
  }

  const agentType =
    isSkillMode
      ? DarwinAgentType.ANALYST_CHAT_AGENT
      : (agentConfig.header?.type
          ? (darwinAgentTypeFromJSON(agentConfig.header.type) as DarwinAgentType)
          : undefined);

  return (
    <Box bg="gray.100" p={2} height="100vh" overflow="hidden">
      <Grid
        height="100%"
        width="100%"
        templateColumns="minmax(0, 25%) minmax(0, 75%)"
        gap={1}
      >
        <EvaEntityLeftPanel
          {...(isSkillMode ? { skill: skillConfig } : { agent: agentConfig })}
          availableTools={availableTools}
          availableSkills={availableSkills}
          onConfigChange={handleConfigChange}
          onToolsChange={handleToolsChange}
          onSkillsChange={handleSkillsChange}
          isEditing={isEditing}
          onEdit={handleEdit}
          onCancel={handleCancel}
          onSave={handleSave}
          onViewDocs={handleViewDocs}
        />

        <EvaPromptRightPanelWrapper
          {...(isSkillMode ? { skill: skillConfig } : { agent: agentConfig })}
          promptConfig={promptConfig}
          promptEditor={promptEditor}
          onPromptEditorChange={handlePromptEditorChange}
          onPromptConfigChange={handlePromptConfigChange}
          analysis_flag=""
          onViewDocs={handleViewDocs}
        />
      </Grid>

      <DocModal
        isDocsOpen={isDocsOpen}
        onDocsClose={onDocsClose}
        isLoadingDoc={isLoadingDoc}
        availableVariables={availableVariables}
        agentType={agentType}
        isSkillMode={isSkillMode}
      />
    </Box>
  );
};

export default EvaEntityDetailContainer;
